// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@mcmc/XDL/Helpers";
import { ellipsis, Roboto } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

interface ProductListLayoutStyles {
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
}

export const productListLayout: ProductListLayoutStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            margin: -15px;
        `,
    },
    leftSection: {
        css: css`
            padding: 15px;
            margin-top: 5px;
            flex: 0 0 320px;
            min-width: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex: 1 1 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    rightSection: {
        css: css`
            flex: 1 1 550px;
            padding: 15px;
            min-width: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex: 1 1 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    topSectionWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    selectWrapper?: InjectableCss;
    categoriesSort?: SelectPresentationProps;
    categoriesContentWrapper?: InjectableCss;
    styledBox?: InjectableCss;
    categoryNameWrapper?: InjectableCss;
    categoryName?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    categoryImageWrapper?: InjectableCss;
}

export const categoryList: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    topSectionWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding-bottom: 5px;
            margin-bottom: 30px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    titleWrapper: {
        css: css`
            flex: 1;
            margin-right: 10px;
        `,
    },
    selectWrapper: {
        css: css`
            flex: 0 1 260px;
            ${screen("lte", "xs")`
                flex: 1 1 100%;
                margin-bottom: 20px;
            `}
        `,
    },
    categoriesSort: {
        cssOverrides: {
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                label {
                    width: 75px;
                    text-transform: uppercase;
                    padding-right: 10px;
                }
            `,
            formInputWrapper: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                width: calc(100% - 75px);
            `,
        },
    },
    categoriesContentWrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
            grid-gap: 30px;
            @media print {
                display: block;
            }
            ${screen("lte", "xs")`
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                grid-gap: 5px;
            `}
        `,
    },
    styledBox: {
        css: css`
            padding: 0;
            margin-bottom: 0;
            height: 100%;
        `,
    },
    categoryNameWrapper: {
        css: css`
            padding: 9px 20px;
            display: flex;
            flex: 1;
            align-items: flex-end;
            &:first-child {
                align-items: flex-start;
            }
            ${screen("lte", "xs")`
                padding: 7px;
            `}
        `,
    },
    categoryName: {
        ...Roboto,
        color: "text",
        css: css`
            ${ellipsis(2, 17)}
            @media print {
                display: block;
                max-height: none;
            }
            ${screen("lte", "xs")`
                font-size: 13px;
                line-height: 15px;
                max-height: 30px;
            `}
        `,
    },
    link: {
        css: css`
            &,
            > span {
                display: flex;
                flex-direction: column;
                height: 100%;
                width: 100%;
            }
        `,
    },
    categoryImageWrapper: {
        css: css`
            height: 190px;
            display: flex;
            justify-content: center;
            ${screen("lte", "xs")`
                height: 160px;
                padding: 5px 5px 0;
            `}
        `,
    },
};

const categoryListStyles = {
    ...productListLayout,
    ...categoryList,
};

export default categoryListStyles;
